import React, { useState, useEffect } from 'react';
import { Phone, Globe, Mail, MapPin, Share2, Ticket, Send, Copy, Check, MessageSquare } from 'lucide-react';

const ACTION_CONFIG = {
  business: { website: 'Website', phone: 'Call', email: 'Email', address: 'Directions', share: 'Share' },
  job: { website: 'Apply', phone: 'Call', email: 'Email', share: 'Share' },
  event: { website: 'Tickets', phone: 'Call', address: 'Directions', share: 'Share' },
  classified: { phone: 'Call', email: 'Contact', message: 'Send Message', share: 'Share' },
};

const ICONS = {
  phone: Phone,
  website: Globe,
  email: Mail,
  address: MapPin,
  share: Share2,
};

const ICON_OVERRIDES = {
  job: { website: Send },
  event: { website: Ticket },
};

export default function MobileQuickActions({ listing }) {
  const [copied, setCopied] = useState(false);
  const [hidden, setHidden] = useState(false);

  // Hide bar when user scrolls to contact section
  useEffect(() => {
    const contactEl = document.querySelector('[data-testid="contact-section"]');
    if (!contactEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHidden(entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(contactEl);
    return () => observer.disconnect();
  }, [listing]);

  if (!listing) return null;

  const type = listing.listing_type || 'business';
  const config = ACTION_CONFIG[type] || ACTION_CONFIG.business;

  // Build actions based on available data
  const actions = [];

  if (config.phone && listing.phone && listing.status !== 'sold') {
    actions.push({
      key: 'phone',
      label: config.phone,
      href: `tel:${listing.phone}`,
      icon: ICONS.phone,
    });
  }

  if (config.website && listing.website && type !== 'classified') {
    const url = listing.website.startsWith('http') ? listing.website : `https://${listing.website}`;
    const IconComp = ICON_OVERRIDES[type]?.website || ICONS.website;
    actions.push({
      key: 'website',
      label: config.website,
      href: url,
      target: '_blank',
      icon: IconComp,
    });
  }

  // Send Message for marketplace/classified listings
  if (config.message && type === 'classified' && listing.status !== 'sold') {
    actions.push({
      key: 'message',
      label: config.message,
      onClick: () => {
        // Dispatch event — ListingDetail handles both logged-in (show form) and logged-out (show signup modal)
        window.dispatchEvent(new CustomEvent('mobile-send-message'));
      },
      icon: MessageSquare,
    });
  }

  if (config.email && listing.email && listing.status !== 'sold') {
    actions.push({
      key: 'email',
      label: config.email,
      href: `mailto:${listing.email}`,
      icon: ICONS.email,
    });
  }

  if (config.address && listing.address && listing.status !== 'sold' && type !== 'classified') {
    const q = encodeURIComponent(`${listing.address}${listing.town ? ', ' + listing.town : ''}, Essex`);
    actions.push({
      key: 'address',
      label: config.address,
      href: `https://www.google.com/maps/search/?api=1&query=${q}`,
      target: '_blank',
      icon: ICONS.address,
    });
  }

  // Share is always available
  const handleShare = async () => {
    const url = window.location.href;
    const title = listing.title || 'Check this out';
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {}
    }
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  actions.push({
    key: 'share',
    label: copied ? 'Copied' : 'Share',
    onClick: handleShare,
    icon: copied ? Check : Share2,
  });

  // Don't render if only share action (no contact info at all)
  if (actions.length <= 1) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-transform duration-300 ${hidden ? 'translate-y-full' : 'translate-y-0'}`}
      data-testid="mobile-quick-actions"
    >
      <div className="bg-white/95 backdrop-blur-lg border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-3 py-2.5 safe-bottom">
        <div className="flex items-center justify-around gap-1 max-w-lg mx-auto">
          {actions.map((action) => {
            const Icon = action.icon;
            const isShare = action.key === 'share';
            const isPrimary = action.key === 'phone' || action.key === 'message' || (action.key === 'website' && !actions.find(a => a.key === 'phone'));

            if (action.onClick) {
              return (
                <button
                  key={action.key}
                  onClick={action.onClick}
                  className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors min-w-0 flex-1 ${
                    isPrimary ? 'bg-[#0F2C3E] active:bg-[#1a3d52]' : 'active:bg-slate-100'
                  }`}
                  data-testid={`quick-action-${action.key}`}
                >
                  <Icon className={`h-5 w-5 ${isPrimary ? 'text-white' : isShare ? 'text-slate-500' : 'text-[#0F2C3E]'}`} />
                  <span className={`text-[10px] font-medium truncate ${isPrimary ? 'text-white' : 'text-slate-600'}`}>{action.label}</span>
                </button>
              );
            }

            return (
              <a
                key={action.key}
                href={action.href}
                target={action.target}
                rel={action.target === '_blank' ? 'noopener noreferrer' : undefined}
                className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-colors active:bg-slate-100 min-w-0 flex-1 no-underline ${
                  isPrimary ? 'bg-[#0F2C3E] active:bg-[#1a3d52]' : ''
                }`}
                data-testid={`quick-action-${action.key}`}
              >
                <Icon className={`h-5 w-5 ${isPrimary ? 'text-white' : 'text-[#0F2C3E]'}`} />
                <span className={`text-[10px] font-medium truncate ${isPrimary ? 'text-white' : 'text-slate-600'}`}>{action.label}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
